import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PillBadge, GhostButton } from "./Primitives";
import { fmtDateTime } from "../lib/tz";
import { AlertOctagon, ArrowRight } from "lucide-react";

const SEVERITY_TONE = { critical: "red", high: "red", medium: "amber", low: "blue" };

/**
 * PortalRecentIncidents — condensed recent-incident feed for the client
 * dashboard. Respects the caller-provided rolling window so it stays in sync
 * with the metrics grid above it. Caps at 10 rows with a "View all" link to
 * the full portal reports page.
 */
export default function PortalRecentIncidents({ days, startDate, endDate }) {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = { limit: 10 };
        if (startDate && endDate) {
          params.start = new Date(startDate + "T00:00:00").toISOString();
          params.end = new Date(endDate + "T23:59:59").toISOString();
        } else {
          params.days = days;
        }
        const r = await api.get("/portal/incidents", { params });
        if (!cancelled) { setRows(Array.isArray(r.data) ? r.data : []); setErr(false); }
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, [days, startDate, endDate]);

  return (
    <section data-testid="portal-recent-incidents" className="border border-[#27272A] bg-[#121212]">
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center justify-between">
        <div>
          <div className="label-mute flex items-center gap-2">
            <AlertOctagon className="size-3.5" />
            Latest Reports
          </div>
          <div className="font-display uppercase font-bold text-xl mt-1">Recent Incidents</div>
        </div>
        <GhostButton
          data-testid="portal-recent-incidents-view-all"
          onClick={() => nav("/portal/reports/incidents")}
        >
          View all <ArrowRight className="size-3.5" />
        </GhostButton>
      </div>

      <div className="divide-y divide-[#27272A]">
        {rows === null && !err && (
          <div className="p-6 text-zinc-500 text-sm">Loading…</div>
        )}
        {err && (
          <div className="p-6 text-zinc-500 text-sm">Feed unavailable.</div>
        )}
        {rows !== null && rows.length === 0 && !err && (
          <div
            data-testid="portal-recent-incidents-empty"
            className="p-8 text-center text-zinc-500 text-sm scanlines"
          >
            No incidents at your sites in this window. All clear.
          </div>
        )}
        {rows && rows.slice(0, 10).map((i) => {
          const tone = SEVERITY_TONE[i.severity] || "default";
          return (
            <button
              key={i.id}
              type="button"
              onClick={() => nav("/portal/reports/incidents")}
              data-testid={`portal-recent-incident-${i.id}`}
              className="w-full text-left p-4 hover:bg-[#0A0A0A] transition-colors flex items-start gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <PillBadge tone={tone}>{i.severity || "unknown"}</PillBadge>
                  <div className="text-sm font-medium truncate text-zinc-100">
                    {i.title || i.incident_type || "Incident"}
                  </div>
                </div>
                <div className="mt-1 flex items-center gap-3 text-xs text-zinc-500 font-mono">
                  <span className="truncate">{i.site_name || "—"}</span>
                  <span className="text-zinc-700">·</span>
                  <span>{fmtDateTime(i.created_at, i.site_timezone)}</span>
                </div>
              </div>
              <ArrowRight className="size-4 text-zinc-600 shrink-0 mt-1" />
            </button>
          );
        })}
      </div>
    </section>
  );
}
